import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Leaf, Brain, Zap, Search, Globe, ArrowRight, LogIn, Quote, HelpCircle,
  Camera, Sparkles, ListChecks, ShieldCheck, Timer, Smartphone,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { TodayTasks } from '@/components/home/TodayTasks';
import { OnboardingHero } from '@/components/home/OnboardingHero';
import { NotificationBanner } from '@/components/home/NotificationBanner';
import { ExploreCards } from '@/components/home/ExploreCards';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/gtag';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plantCount, setPlantCount] = useState<number | null>(null);
  const [showStickyCta, setShowStickyCta] = useState(false);

  useEffect(() => {
    if (!user) {
      setPlantCount(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { count } = await supabase
        .from('plant_identifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id);
      if (cancelled) return;
      const c = count ?? 0;
      setPlantCount(c);
      trackEvent(c === 0 ? 'home_viewed_first_time' : 'home_viewed_returning', { plant_count: c });
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (user) return;
    const onScroll = () => setShowStickyCta(window.scrollY > 320);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [user]);

  const features = [
    { icon: Brain, title: 'Processamento Neural', description: 'Raciocínio visual de ponta em botânica.' },
    { icon: Zap, title: 'Diagnóstico Instantâneo', description: 'Pragas e espécies em milissegundos.' },
    { icon: Search, title: 'Análise Multimodal', description: 'Deficiências e luz por foto.' },
    { icon: Globe, title: 'Web-app Progressivo', description: 'Sem instalar, em qualquer navegador.' },
  ];

  const steps = [
    { icon: Camera, title: 'Fotografe', desc: 'Use a câmera do celular direto pelo app.' },
    { icon: Sparkles, title: 'IA analisa', desc: 'Modelos de visão identificam a espécie e o estado.' },
    { icon: ListChecks, title: 'Receba cuidados', desc: 'Plano de rega, luz e diagnóstico personalizados.' },
  ];

  const pillars = [
    { icon: ShieldCheck, title: 'Precisão científica', desc: 'Modelos líderes em benchmarks de visão.' },
    { icon: Timer, title: 'Resposta em segundos', desc: 'Sem espera, sem fricção.' },
    { icon: Smartphone, title: 'Sem instalação', desc: 'Funciona no navegador do seu celular.' },
  ];

  const faqs = [
    {
      question: 'O VerdeIA é confiável?',
      answer: 'Sim. Usamos modelos líderes em benchmarks internacionais de visão computacional aplicada a botânica.',
    },
    {
      question: 'Tem versão gratuita?',
      answer: 'Sim. Você pode identificar até 3 plantas por mês gratuitamente, sem precisar de cartão de crédito.',
    },
    {
      question: 'Preciso instalar algum aplicativo?',
      answer: 'Não. O VerdeIA é um web-app e funciona instantaneamente no navegador. Se quiser, dá para adicionar à tela inicial como um app.',
    },
    {
      question: 'Minhas fotos ficam privadas?',
      answer: 'Sim. As imagens são usadas apenas para a análise da sua planta e ficam vinculadas só à sua conta.',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      {user ? (
        <MobileHeader showLogo />
      ) : (
        <header className="sticky top-0 z-50 h-14 flex items-center justify-between px-4 bg-background/95 backdrop-blur-sm border-b border-border safe-area-pt">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <Leaf className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-display font-semibold">VerdeIA</span>
          </div>
          <Button onClick={() => navigate('/auth')} variant="outline" size="sm" className="h-9 press-scale">
            <LogIn className="mr-2 h-4 w-4" />
            Entrar
          </Button>
        </header>
      )}

      {user ? (
        // === VERSÃO LOGADO ===
        <>
          <NotificationBanner />

          {plantCount === 0 ? (
            <OnboardingHero />
          ) : (
            <>
              <TodayTasks />

              <section className="px-4 pt-4 pb-2">
                <div className="max-w-lg mx-auto">
                  <Button
                    size="lg"
                    onClick={() => navigate('/identify')}
                    className="w-full h-12 gradient-nature text-primary-foreground press-scale touch-target"
                  >
                    <Brain className="mr-2 h-5 w-5" />
                    Identificar nova planta
                  </Button>
                </div>
              </section>

              <ExploreCards plantCount={plantCount ?? 0} />
            </>
          )}
        </>
      ) : (
        // === VERSÃO NÃO LOGADO ===
        <>
          {/* Hero */}
          <section className="relative overflow-hidden">
            <div className="absolute inset-0 leaf-pattern opacity-30" />
            <div className="px-4 py-8 sm:py-12 md:py-20 relative">
              <div className="max-w-lg mx-auto text-center">
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-full text-xs font-medium mb-4">
                  <Brain className="h-3 w-3" />
                  Powered by IA
                </div>

                <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4 font-display leading-tight">
                  <span className="text-primary">VerdeIA</span>: A maneira mais fácil de cuidar do seu jardim.
                </h1>

                <p className="text-sm sm:text-base text-muted-foreground mb-6 max-w-sm mx-auto">
                  Identifique espécies e diagnostique a saúde das suas plantas com o que há de mais avançado em visão computacional.
                </p>

                <div className="flex flex-col gap-2">
                  <Button
                    size="lg"
                    onClick={() => navigate('/auth?redirect=/identify')}
                    className="w-full h-12 gradient-nature text-primary-foreground press-scale touch-target"
                  >
                    <Brain className="mr-2 h-5 w-5" />
                    Analisar minha planta agora
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Grátis para começar · Sem cartão de crédito
                  </p>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-10 -left-10 w-32 sm:w-64 h-32 sm:h-64 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute -top-10 -right-10 w-32 sm:w-64 h-32 sm:h-64 bg-accent/10 rounded-full blur-3xl" />
          </section>

          {/* Como funciona */}
          <section className="py-8 sm:py-12 md:py-16 px-4 bg-muted/30">
            <div className="max-w-lg mx-auto">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-center text-foreground mb-6 font-display">
                Como funciona
              </h2>
              <ol className="space-y-3">
                {steps.map((s, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 p-4 rounded-2xl bg-card border border-border"
                  >
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-display font-semibold">
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <s.icon className="h-4 w-4 text-primary" />
                        <h3 className="font-semibold text-sm sm:text-base text-foreground">{s.title}</h3>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground">{s.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          {/* Features - grid 2x2 no mobile */}
          <section className="py-8 sm:py-12 md:py-20 px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-center text-foreground mb-6 sm:mb-8 font-display">
                Tecnologia de ponta
              </h2>

              <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="text-center p-4 sm:p-6 rounded-2xl bg-card glass-card animate-fade-in"
                    style={{ animationDelay: `${index * 80}ms` }}
                  >
                    <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full gradient-nature flex items-center justify-center mx-auto mb-2 sm:mb-3">
                      <feature.icon className="h-5 w-5 sm:h-7 sm:w-7 text-primary-foreground" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-1 text-xs sm:text-base leading-tight">{feature.title}</h3>
                    <p className="text-[11px] sm:text-sm text-muted-foreground leading-snug">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Por que VerdeIA - benefícios consolidados */}
          <section className="py-8 sm:py-12 md:py-16 px-4 bg-muted/30">
            <div className="max-w-lg mx-auto">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-6 font-display text-center">
                Por que <span className="text-primary">VerdeIA</span>
              </h2>
              <div className="space-y-3">
                {pillars.map((p, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <p.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground text-sm sm:text-base">{p.title}</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Prova social */}
          <section className="py-8 sm:py-12 md:py-16 px-4">
            <div className="max-w-lg mx-auto">
              <div className="relative p-6 sm:p-8 rounded-2xl bg-card glass-card">
                <Quote className="absolute top-4 left-4 h-8 w-8 text-primary/20" />
                <blockquote className="text-center">
                  <p className="text-sm sm:text-base text-foreground italic mb-4 pt-4">
                    "A precisão do VerdeIA para identificar doenças foliares é impressionante. Mudou minha forma de gerir meu jardim."
                  </p>
                  <footer className="text-xs sm:text-sm text-muted-foreground font-medium">
                    — Julio V., Paisagista
                  </footer>
                </blockquote>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-8 sm:py-12 md:py-16 px-4 bg-muted/30">
            <div className="max-w-lg mx-auto">
              <div className="flex items-center justify-center gap-2 mb-6">
                <HelpCircle className="h-5 w-5 text-primary" />
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground font-display">
                  Perguntas Frequentes
                </h2>
              </div>

              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border-border">
                    <AccordionTrigger className="text-sm sm:text-base text-foreground hover:text-primary text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          {/* CTA final */}
          <section className="py-8 sm:py-12 md:py-20 px-4">
            <div className="max-w-sm mx-auto p-6 sm:p-8 rounded-2xl sm:rounded-3xl gradient-nature text-primary-foreground">
              <Leaf className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4" />
              <h2 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-4 font-display text-center">
                Comece agora
              </h2>
              <p className="mb-4 sm:mb-6 opacity-90 text-xs sm:text-sm text-center">
                Grátis para começar. Sem cartão de crédito.
              </p>
              <Button
                size="lg"
                variant="secondary"
                onClick={() => navigate('/auth?redirect=/identify')}
                className="w-full h-12 press-scale touch-target"
              >
                Começar agora com VerdeIA
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </section>
        </>
      )}

      {/* Footer */}
      <footer className={`border-t border-border py-6 sm:py-8 ${user ? 'pb-24' : (showStickyCta ? 'pb-24 sm:pb-8' : '')}`}>
        <div className="px-4 text-center text-xs sm:text-sm text-muted-foreground">
          <p>© 2025 VerdeIA. Feito com 💚 para amantes de plantas.</p>
        </div>
      </footer>

      {/* CTA sticky mobile (somente não logado) */}
      {!user && showStickyCta && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 sm:hidden bg-background/95 backdrop-blur-sm border-t border-border px-4 pt-3 animate-fade-in"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
        >
          <Button
            size="lg"
            onClick={() => navigate('/auth?redirect=/identify')}
            className="w-full h-12 gradient-nature text-primary-foreground press-scale touch-target"
          >
            <Brain className="mr-2 h-5 w-5" />
            Analisar minha planta
          </Button>
        </div>
      )}

      {user && <BottomNavigation />}
    </div>
  );
}
